/**
 * Auto Search Within Map Plugin for Search Results Map Variant
 *
 * Watches the backend-suggested bounds (mapBoundariesUpdated$ — the area covering the
 * original search's farthest markers) against the current viewport and, once the map
 * goes idle after a real user gesture, activates a rectangular-area search once the
 * viewport has drifted into new territory the original search never covered (see
 * services/geometry.js:getOutsideAreaRatio). Pure zoom in/out within the original
 * area never counts as drift, no matter how far — only panning past it does.
 *
 * Single responsibility: decide WHEN a rectangular-area search should turn itself on
 * automatically, based on viewport drift. It does not decide what a rectangular-area
 * search looks like once active — that machinery (button visibility, re-fetch on move,
 * min zoom) already lives in searchWithinMap.plugin and reacts to the locationType
 * change this plugin triggers.
 *
 * This plugin does NOT:
 * - Build or dispatch anything when the location type is already RectangularArea
 *   (the trigger condition is inherently satisfied already, so it stays quiet)
 * - Build or dispatch anything on State SRP: State has its own viewport-clamped
 *   auto-refetch flow (viewportAutoSearch.plugin) that must keep locationType as
 *   State, and Search Within Map is intentionally hidden for State everywhere else
 *   (searchWithinMap.plugin's subscribeToButtonVisibility) — auto-flipping State to
 *   RectangularArea here would silently contradict both.
 * - Create or configure the map instance (see map.plugin)
 * - Manage the "search within map" button or re-fetch on subsequent map moves once
 *   rectangular-area is active (see searchWithinMap.plugin)
 * - Manage the KML boundary layer or map positioning (see mapBoundaries.plugin)
 *
 * Only counts real user gestures (drag, scroll/pinch zoom, double-click zoom, zoom-control
 * click) toward activation, mirroring focusedSearch.plugin — a programmatic camera move
 * (e.g. mapBoundaries.plugin's fitBounds on KML load) must never flip the mode on its own.
 *
 * The cached backend bounds only update on a genuine location selection (locationSelected$),
 * never on the mapBoundariesUpdated$ emissions caused by focused-search viewport narrowing,
 * State's viewport-clamped panning, or rectangular-area re-fetches — those describe the
 * current viewport, not the originally searched area, and caching them would make the
 * drift check chase the viewport instead of measuring drift away from it.
 *
 * Reason to change: how "outside the backend bounds" is measured, what ratio triggers
 * activation, or which location types are exempt from auto-activation.
 *
 * @module searchResultsMap/plugins/autoSearchWithinMap.plugin
 */

import { distinctUntilChanged, debounceTime, filter, map } from 'rxjs';
import { LocationType, searchParametersActions, selectLocationType } from 'features/searchResultsFacets';
import { selectGeoCoordinates } from '../redux/selectors';
import createFetchLocationsPayload from '../services/rectangularAreaSearchPayload';
import { areBoundsValid, getOutsideAreaRatio } from '../services/geometry';
import { mapBoundariesUpdated$, locationSelected$ } from '../redux/observables';
import { AUTO_SEARCH_WITHIN_MAP_OUTSIDE_RATIO_THRESHOLD } from '../constants';
import createMapGestureHandlers from '../handlers/mapGestureHandlers';

const RECTANGULAR_AREA = LocationType.RectangularArea.toString();
const STATE = LocationType.State.toString();

const normalizeBackendBounds = ({ minLatitude, maxLatitude, minLongitude, maxLongitude } = {}) => ({
  south: minLatitude,
  north: maxLatitude,
  west: minLongitude,
  east: maxLongitude,
});

export default function createAutoSearchWithinMapPlugin() {
  const state = {
    started: false,
    subscriptions: [],
    mapFacade: null,
    store$: null,
    dispatch: null,
    getState: null,
    gestureHandlers: createMapGestureHandlers(),
    hasUserGesture: false,
    latestBackendBounds: null,
    // Starts true so the very first mapBoundariesUpdated$ emission (the initial page
    // load, which never goes through locationSelected$ — see fetchMarkersResultsEpic's
    // initialFetch$) is still captured. Re-armed only by a genuine location selection.
    awaitingLocationBounds: true,
  };

  function onUserGesture() {
    state.hasUserGesture = true;
  }

  function armBoundsCapture() {
    state.awaitingLocationBounds = true;
  }

  function cacheBackendBounds(data) {
    // Ignore emissions caused by anything other than a genuine new location selection
    // (focused-search viewport narrowing, State's viewport-clamped panning, rectangular-
    // area re-fetches while dragging) — those all emit mapBoundariesUpdated$ too, but
    // their bounds describe the current viewport, not the original searched area, and
    // caching them would make the drift check chase the viewport instead of measuring
    // drift away from it.
    if (!state.awaitingLocationBounds) {
      return;
    }
    state.awaitingLocationBounds = false;

    const bounds = data?.bounds ? normalizeBackendBounds(data.bounds) : null;
    state.latestBackendBounds = areBoundsValid(bounds) ? bounds : null;
  }

  function activateRectangularAreaSearch(viewport) {
    const zoom = state.mapFacade?.getCameraSnapshot?.()?.zoom ?? 0;
    const payload = createFetchLocationsPayload(viewport, zoom);
    if (!payload) {
      return;
    }

    state.dispatch(searchParametersActions.fetchLocations(payload));
  }

  function handleMapIdle(viewport) {
    // Each settled camera position requires its own fresh gesture — a drag from
    // several moves ago must not authorize a later, unrelated programmatic idle.
    const hadUserGesture = state.hasUserGesture;
    state.hasUserGesture = false;

    if (!hadUserGesture || !state.latestBackendBounds) {
      return;
    }

    const outsideRatio = getOutsideAreaRatio(viewport, state.latestBackendBounds);
    if (outsideRatio >= AUTO_SEARCH_WITHIN_MAP_OUTSIDE_RATIO_THRESHOLD) {
      activateRectangularAreaSearch(viewport);
    }
  }

  function isEligibleLocationType() {
    const locationType = selectLocationType(state.getState());
    return locationType !== RECTANGULAR_AREA && locationType !== STATE;
  }

  function subscribeToBackendBounds() {
    state.subscriptions.push(
      mapBoundariesUpdated$.subscribe(cacheBackendBounds),
    );
  }

  function subscribeToLocationSelected() {
    state.subscriptions.push(
      locationSelected$.subscribe(armBoundsCapture),
    );
  }

  function subscribeToMapMoves() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectGeoCoordinates),
        filter(areBoundsValid),
        distinctUntilChanged(),
        debounceTime(300),
        filter(isEligibleLocationType),
      ).subscribe(handleMapIdle),
    );
  }

  function clearSubscriptions() {
    state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    state.subscriptions = [];
  }

  return {
    name: 'auto-search-within-map',

    async init(context) {
      context.state.autoSearchWithinMap = {
        started: false,
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      state.store$ = context.services.store$;
      state.dispatch = context.services.dispatch;
      state.getState = context.services.getState;

      if (!state.mapFacade || !state.store$ || !state.dispatch || !state.getState) {
        return;
      }

      state.gestureHandlers.bind(state.mapFacade, context.root, { onUserGesture });

      subscribeToBackendBounds();
      subscribeToLocationSelected();
      subscribeToMapMoves();

      state.started = true;
      context.state.autoSearchWithinMap = {
        started: true,
      };
    },

    async stop(context) {
      clearSubscriptions();
      state.gestureHandlers.unbind();

      state.started = false;
      state.mapFacade = null;
      state.store$ = null;
      state.dispatch = null;
      state.getState = null;
      state.hasUserGesture = false;
      state.latestBackendBounds = null;
      state.awaitingLocationBounds = true;

      delete context.state.autoSearchWithinMap;
    },
  };
}
